/**
 * Resume Scorecard — deterministic, client-side resume quality analysis.
 *
 * Runs entirely in the browser on the in-memory ResumeJSON (no API call, no
 * cost, works on unsaved edits). The headline dimension is "Authenticity": how
 * human (vs. AI-generated) the writing reads, measured with the SAME ban-list of
 * AI-tell words/structures used by the generation prompts. Higher = more human.
 *
 * This is a guide, not a gate. It detects patterns; it does not rewrite.
 */
import type { ResumeJSON } from '../types';

export type Severity = 'high' | 'med' | 'low';

export interface Finding {
  severity: Severity;
  label: string;      // what was found, e.g. '"results-driven"'
  section: string;    // where, e.g. 'Summary' / 'Experience — Acme Inc.'
  detail?: string;    // optional snippet for context
}

export interface DimensionScore {
  key: 'authenticity' | 'quantification' | 'completeness';
  label: string;
  score: number | null;   // 0-100, or null when not applicable
  blurb: string;
  findings: Finding[];
}

export interface Scorecard {
  overall: number;
  dimensions: DimensionScore[];
}

// ── Prose collection ───────────────────────────────────────────────────────
interface Snippet { text: string; section: string; isBullet: boolean; }

const included = (v?: string) => v !== '0';

function collectProse(resume: ResumeJSON): Snippet[] {
  const out: Snippet[] = [];
  if (resume.summary?.trim()) out.push({ text: resume.summary, section: 'Summary', isBullet: false });
  (resume.highlights || []).forEach((h) => {
    if (h?.trim()) out.push({ text: h, section: 'Highlights', isBullet: true });
  });
  (resume.summary_phrases || []).forEach((p) => {
    if (included(p.include) && p.text?.trim()) out.push({ text: p.text, section: 'Summary phrase', isBullet: false });
  });
  (resume.experience || []).forEach((co) => {
    if (!included(co.include)) return;
    const cname = co.company || 'Experience';
    (co.roles || []).forEach((role) => {
      if (!included(role.include)) return;
      (role.bullets || []).forEach((b) => {
        if (included(b.include) && b.text?.trim()) {
          out.push({ text: b.text, section: `Experience — ${cname}`, isBullet: true });
        }
      });
    });
  });
  return out;
}

const wordCount = (s: string) => (s.trim().match(/\S+/g) || []).length;

// ── Authenticity: AI-tell detection ────────────────────────────────────────
// Weight by how strongly the term signals AI authorship. Ambiguous words that
// can be legitimate (robust, dynamic, elevate…) are 'low' so they nudge rather
// than dominate; hollow phrases that almost never appear in genuine human resume
// writing are 'high'.
const BANNED: { re: RegExp; label: string; severity: Severity }[] = [
  { re: /\bresults[-\s]driven\b/gi, label: '"results-driven"', severity: 'high' },
  { re: /\bproven track record\b/gi, label: '"proven track record"', severity: 'high' },
  { re: /\btrack record of\b/gi, label: '"track record of"', severity: 'med' },
  { re: /\bpassionate(ly)?\b/gi, label: '"passionate"', severity: 'med' },
  { re: /\bleverag(e|es|ed|ing)\b/gi, label: '"leverage"', severity: 'high' },
  { re: /\bspearhead(ed|ing|s)?\b/gi, label: '"spearheaded"', severity: 'high' },
  { re: /\bdemonstrated ability\b/gi, label: '"demonstrated ability"', severity: 'high' },
  { re: /\bdeep expertise\b/gi, label: '"deep expertise"', severity: 'med' },
  { re: /\bseamless(ly)?\b/gi, label: '"seamless"', severity: 'med' },
  { re: /\bcutting[-\s]edge\b/gi, label: '"cutting-edge"', severity: 'med' },
  { re: /\bsynerg(y|ies|istic)\b/gi, label: '"synergy"', severity: 'high' },
  { re: /\bin today'?s fast[-\s]paced\b/gi, label: '"in today\'s fast-paced…"', severity: 'high' },
  { re: /\bwheelhouse\b/gi, label: '"wheelhouse"', severity: 'med' },
  { re: /\btapestry\b/gi, label: '"tapestry"', severity: 'high' },
  { re: /\btestament to\b/gi, label: '"testament to"', severity: 'med' },
  { re: /\bnavigate the complexities\b/gi, label: '"navigate the complexities"', severity: 'high' },
  { re: /\bunleash(ing|ed|es)?\b/gi, label: '"unleash"', severity: 'med' },
  { re: /\bunlock(ing|ed|s)?\b/gi, label: '"unlock"', severity: 'low' },
  { re: /\belevat(e|ed|es|ing)\b/gi, label: '"elevate"', severity: 'low' },
  { re: /\brobust\b/gi, label: '"robust"', severity: 'low' },
  { re: /\bdynamic\b/gi, label: '"dynamic"', severity: 'low' },
];

const PENALTY: Record<Severity, number> = { high: 8, med: 5, low: 2 };

function analyzeAuthenticity(snippets: Snippet[]): DimensionScore {
  const findings: Finding[] = [];
  let penalty = 0;

  // 1. Banned words / phrases
  for (const s of snippets) {
    for (const b of BANNED) {
      const m = s.text.match(b.re);
      if (m) {
        for (let i = 0; i < m.length; i++) {
          penalty += PENALTY[b.severity];
          findings.push({ severity: b.severity, label: `AI-tell word ${b.label}`, section: s.section });
        }
      }
    }
  }

  // 2. "not only … but also"
  for (const s of snippets) {
    const m = s.text.match(/\bnot only\b[^.!?]*\bbut also\b/gi);
    if (m) m.forEach(() => { penalty += 6; findings.push({ severity: 'med', label: '"not only … but also" construction', section: s.section }); });
  }

  // 3. Rule-of-three serial lists (a, b, and c) — sentence has ≥2 commas + "and"
  for (const s of snippets) {
    s.text.split(/(?<=[.!?])\s+/).forEach((sent) => {
      const commas = (sent.match(/,/g) || []).length;
      if (commas >= 2 && /,\s+(?:and|or)\s+\w/i.test(sent)) {
        penalty += 4;
        findings.push({ severity: 'low', label: 'Rule-of-three list ("X, Y, and Z")', section: s.section, detail: sent.trim().slice(0, 90) });
      }
    });
  }

  // 4. Em-dash clause splices
  for (const s of snippets) {
    const m = s.text.match(/\s[—–]\s|\s-\s/g);
    if (m && m.length) { penalty += 2 * m.length; findings.push({ severity: 'low', label: `Em-dash splice ×${m.length}`, section: s.section }); }
  }

  // 5. Participle-opening density across bullets ("Leading…", "Driving…")
  const bullets = snippets.filter((s) => s.isBullet);
  if (bullets.length >= 3) {
    const ing = bullets.filter((b) => /^\s*[A-Z][a-z]+ing\b/.test(b.text));
    const ratio = ing.length / bullets.length;
    if (ratio > 0.35) {
      penalty += Math.round((ratio - 0.35) * 60);
      findings.push({ severity: 'med', label: `${Math.round(ratio * 100)}% of bullets open with an "-ing" participle (monotone rhythm)`, section: 'Experience / Highlights' });
    }
  }

  const totalWords = snippets.reduce((n, s) => n + wordCount(s.text), 0);
  const score = totalWords === 0 ? null : Math.max(0, Math.min(100, 100 - penalty));

  let blurb: string;
  if (score === null) blurb = 'No written content yet to evaluate.';
  else if (score >= 90) blurb = 'Reads human and specific — very few AI tells.';
  else if (score >= 75) blurb = 'Mostly natural; a handful of AI tells worth trimming.';
  else if (score >= 50) blurb = 'Several AI-sounding patterns — worth a editing pass.';
  else blurb = 'Reads strongly AI-generated; recruiters may notice.';

  // Sort findings high→low so the worst show first; de-dupe identical lines.
  const order: Record<Severity, number> = { high: 0, med: 1, low: 2 };
  findings.sort((a, b) => order[a.severity] - order[b.severity]);

  return { key: 'authenticity', label: 'Authenticity', score, blurb, findings };
}

// ── Quantification: do bullets carry concrete numbers? ──────────────────────
const NUMBERY = /\d|%|\$|\b(one|two|three|four|five|six|seven|eight|nine|ten|dozens?|hundreds?|thousands?|millions?|billions?|k\b|m\b|x\b)\b/i;

function analyzeQuantification(snippets: Snippet[]): DimensionScore {
  const bullets = snippets.filter((s) => s.isBullet && s.section.startsWith('Experience'));
  if (bullets.length === 0) {
    return { key: 'quantification', label: 'Quantification', score: null, blurb: 'No experience bullets to evaluate.', findings: [] };
  }
  const findings: Finding[] = [];
  let quantified = 0;
  for (const b of bullets) {
    if (NUMBERY.test(b.text)) quantified++;
    else if (findings.length < 8) findings.push({ severity: 'low', label: 'Bullet has no number/metric', section: b.section, detail: b.text.slice(0, 90) });
  }
  const score = Math.round((quantified / bullets.length) * 100);
  const blurb = `${quantified} of ${bullets.length} experience bullets (${score}%) include a concrete number or metric.`;
  return { key: 'quantification', label: 'Quantification', score, blurb, findings };
}

// ── Completeness: is the core resume filled in? ─────────────────────────────
function analyzeCompleteness(resume: ResumeJSON): DimensionScore {
  const checks: { ok: boolean; label: string }[] = [
    { ok: !!resume.name?.trim(), label: 'Name' },
    { ok: (resume.contact || []).length > 0, label: 'At least one contact method' },
    { ok: !!resume.summary?.trim(), label: 'Summary' },
    {
      ok: (resume.experience || []).some((c) => included(c.include) && (c.roles || []).some((r) => (r.bullets || []).some((b) => b.text?.trim()))),
      label: 'At least one experience bullet',
    },
    { ok: (resume.skills || []).filter((s) => s.title?.trim()).length >= 3, label: 'At least 3 skills' },
    { ok: (resume.education || []).length > 0, label: 'At least one education entry' },
  ];
  const passed = checks.filter((c) => c.ok).length;
  const score = Math.round((passed / checks.length) * 100);
  const findings: Finding[] = checks.filter((c) => !c.ok).map((c) => ({ severity: 'med', label: `Missing: ${c.label}`, section: 'Resume' }));
  const blurb = `${passed} of ${checks.length} core sections present.`;
  return { key: 'completeness', label: 'Completeness', score, blurb, findings };
}

// ── Top-level ───────────────────────────────────────────────────────────────
const WEIGHTS: Record<DimensionScore['key'], number> = { authenticity: 0.5, quantification: 0.25, completeness: 0.25 };

export function buildScorecard(resume: ResumeJSON): Scorecard {
  const snippets = collectProse(resume);
  const dimensions: DimensionScore[] = [
    analyzeAuthenticity(snippets),
    analyzeQuantification(snippets),
    analyzeCompleteness(resume),
  ];
  let wsum = 0, w = 0;
  for (const d of dimensions) {
    if (d.score === null) continue;
    wsum += d.score * WEIGHTS[d.key];
    w += WEIGHTS[d.key];
  }
  const overall = w === 0 ? 0 : Math.round(wsum / w);
  return { overall, dimensions };
}

export function scoreColor(score: number | null): { text: string; bg: string; bar: string } {
  if (score === null) return { text: 'text-gray-400', bg: 'bg-gray-50', bar: 'bg-gray-300' };
  if (score >= 85) return { text: 'text-green-700', bg: 'bg-green-50', bar: 'bg-green-500' };
  if (score >= 70) return { text: 'text-amber-700', bg: 'bg-amber-50', bar: 'bg-amber-500' };
  return { text: 'text-red-700', bg: 'bg-red-50', bar: 'bg-red-500' };
}
